import { FolderOutlined } from '../../../components/icons'
import { Chip } from '../../../components/ui/Chip'
import { EmptyState } from '../../../components/ui/EmptyState'
import { BOM_STATUS_META } from '../../parts/components/BomStatusChip'
import { formatPrice } from '../../parts/format'
import { BOM_STATUS_LABEL } from '../../parts/types'
import type { LibraryBomRow } from '../../parts/types'
import { tintClassForBom } from '../workspace-tint'

export interface WorkspaceBomsTabProps {
  readonly items: readonly LibraryBomRow[]
  readonly onOpen: (id: string) => void
}

/**
 * UID-299 (Slice C) — the Workspace detail page's BOMs tab
 * (`Project Workspace.dc.html:153-179`): a card grid (name, "{count} parts ·
 * {totalLabel}", status chip), NOT the Parts Library's own BOM card grid
 * — the two screens render BOMs differently by design, so this is
 * a second, deliberately distinct presentational component rather than a
 * fork of the Parts Library's `BomCardGrid`/`BomStatusChip`. Reuses the
 * canonical `BOM_STATUS_META`/`BOM_STATUS_LABEL` chip mapping (UID-295/296)
 * so the two surfaces still agree on status color/label.
 *
 * The comp's card also shows a `thumbs` row of constituent-part thumbnails
 * (`:172`) — a flat, non-overlapping row of 34x34 squares, deliberately NOT
 * the overlapping-stack pattern the Orders tab uses elsewhere in the same
 * comp. Backed by `LibraryBomRow.thumbnailUrls` (server-derived, capped at
 * 3); the row is omitted entirely when empty, matching the comp (an empty
 * `thumbs` array renders nothing there either).
 */
export function WorkspaceBomsTab({ items, onOpen }: WorkspaceBomsTabProps) {
  if (items.length === 0) {
    return <EmptyState title="No BOMs in this workspace yet." />
  }

  return (
    <div className="workspace-boms-grid">
      {items.map((bom) => (
        <button
          key={bom.id}
          type="button"
          className="workspace-bom-card"
          onClick={() => onOpen(bom.id)}
        >
          <div className="workspace-bom-card-header">
            <span className={`workspace-bom-icon ${tintClassForBom(bom.id)}`} aria-hidden="true">
              <FolderOutlined />
            </span>
            <div className="workspace-bom-meta">
              <span className="workspace-bom-name">{bom.name}</span>
              <span className="caption-text">
                {bom.partCount} parts · {bom.estimatedCost ? formatPrice(bom.estimatedCost) : '—'}
              </span>
            </div>
          </div>
          <div className="workspace-bom-card-footer">
            <Chip tone={BOM_STATUS_META[bom.status].tone} className="workspace-bom-status-chip">
              {BOM_STATUS_LABEL[bom.status]}
            </Chip>
            {bom.thumbnailUrls && bom.thumbnailUrls.length > 0 && (
              <div className="workspace-bom-thumbs" aria-hidden="true">
                {bom.thumbnailUrls.map((url, index) => (
                  <span
                    key={`${bom.id}-thumb-${index}`}
                    className="workspace-bom-thumb"
                    // Quote the URL inside `url(...)` and escape only quote/
                    // backslash — NOT `encodeURI`, which double-encodes the
                    // `%`-escapes already present in a presigned S3 URL's query
                    // string (signature, date, etc.), corrupting the signature
                    // so S3 rejects the request and the thumbnail silently never
                    // loads (see InProgressSessionCard.tsx's identical fix,
                    // UID-140 Gate 5 F1). Quoting + escaping just the quote/
                    // backslash still closes the original `)`-breakout concern
                    // without touching the URL's own valid encoding.
                    style={{ backgroundImage: `url("${url.replace(/["\\]/g, '\\$&')}")` }}
                  />
                ))}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  )
}
